/**
 * Rock Off Retail - wishlist heart on USF grid cards (30 Sep 2026).
 *
 * The theme's own cards (snippets/card-product.liquid -> snippets/
 * rockoff-wishlist-button.liquid) carry a wishlist heart top-right of the
 * image. Cards on USF-owned grids (/collections/*, /search, rendered by the
 * USF app from window.usf.templates as `li.usf-sr-product`) did not, and
 * the USF template cannot be edited from this repo (a runtime template patch
 * is inert; see BUILD-LOG.md 18 Aug). This script closes the gap from the
 * theme side: it injects the SAME heart into each USF card, so both grids
 * match.
 *
 * Contract with the existing wishlist code (no changes needed there):
 *   - click handling is the delegated listener in assets/rockoff-affinity.js
 *     (bindWishlistButtons), keyed on [data-ro-wishlist-btn] and reading
 *     data-ro-wishlist-id / -handle / -hub / -label / -add-label /
 *     -remove-label. It calls preventDefault, toggles .is-wishlisted and
 *     aria-pressed and rebuilds the aria-label. This script therefore only
 *     builds a button with the identical attribute set and paints the saved
 *     state on render (the theme cards do that with an inline script per card).
 *   - the wishlist is keyed by product HANDLE (window.ROAffinity.isWishlisted).
 *     The handle is read from the card's /products/<handle> link. The numeric
 *     product id (optional, only sent with the Klaviyo event) comes from
 *     data-usf-pid when the card carries it.
 *
 * Placement: the heart is appended to the card's `.card-wrapper` (a sibling
 * of `.card`, exactly like the hub cards), because the theme's stacking
 * notes (card-product.liquid) show a heart nested inside `.card` sits under
 * the heading link's ::after overlay. If the image column does not reach the
 * wrapper's right edge (USF list view) the heart is nudged in so it stays
 * top-right of the IMAGE. Falls back to the image container when a card has
 * no `.card-wrapper`.
 *
 * Localised copy: read from window._roWishlistAddLabel /
 * _roWishlistRemoveLabel / _roWishlistLabel, rendered from Liquid
 * (`rockoff.wishlist.*` locale keys) in snippets/usf.liquid. English is only
 * a last-resort fallback if those globals are missing.
 *
 * Re-render safety: USF (RVue) replaces and patches card nodes on filter,
 * sort, paging and infinite scroll, and reuses nodes for different products.
 * One debounced MutationObserver on document.body (same approach as
 * assets/ro-model-view-toggle.js: #usf_container itself can be replaced, so
 * it is re-resolved on every scan) plus usf.event 'sr_updated' when the USF
 * runtime exposes it. Each scan is idempotent AND incremental (1 Oct 2026):
 * only cards that are new, or that now show a different product (a reused
 * node), are touched, so the cost of a Load more click no longer grows with
 * the number of cards already on the page. One heart per card, and a reused
 * card gets its heart re-pointed at the new product's handle. Resize, load
 * and bfcache restore re-measure every card.
 *
 * DOM is built with createElement / createElementNS only (BUILD-LOG gotcha
 * #82: never innerHTML custom elements). Everything is wrapped so a failure
 * can never break the grid.
 */
(function () {
  'use strict';

  var CONTAINER_ID = 'usf_container';
  var CARD_SELECTOR = '.usf-sr-product';
  var OWN_ATTR = 'data-ro-usf-heart';
  var DONE_ATTR = 'data-ro-usf-heart-for';
  var HEART_PATH = 'M12 21s-7.5-4.6-10-9.1C.4 8.6 2 5 5.6 5c2 0 3.4 1 4.4 2.4C11 6 12.4 5 14.4 5 18 5 19.6 8.6 22 11.9c-2.5 4.5-10 9.1-10 9.1Z';
  var SVG_NS = 'http://www.w3.org/2000/svg';
  var RESCAN_DELAY_MS = 80;
  var AFFINITY_KEY = 'ro_affinity_v1';

  function getContainer() {
    return document.getElementById(CONTAINER_ID);
  }

  function labels() {
    return {
      add: window._roWishlistAddLabel || 'Add to wishlist',
      remove: window._roWishlistRemoveLabel || 'Remove from wishlist',
      name: window._roWishlistLabel || 'Wishlist'
    };
  }

  function handleFor(card) {
    var links = card.querySelectorAll('a[href*="/products/"]');
    for (var i = 0; i < links.length; i++) {
      var href = links[i].getAttribute('href') || '';
      var m = href.match(/\/products\/([^/?#]+)/);
      if (m && m[1]) {
        try {
          return decodeURIComponent(m[1]);
        } catch (e) {
          return m[1];
        }
      }
    }
    return '';
  }

  function idFor(card) {
    var el = card.hasAttribute('data-usf-pid') ? card : card.querySelector('[data-usf-pid]');
    if (!el && card.closest) el = card.closest('[data-usf-pid]');
    var pid = el ? el.getAttribute('data-usf-pid') : '';
    return pid && /^\d+$/.test(pid) ? pid : '';
  }

  function titleFor(card, handle) {
    var el = card.querySelector('.card__heading, .card__heading a, [class*="title"]');
    var text = el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : '';
    if (!text) {
      var img = card.querySelector('img:not(.ro-card-model-media)');
      text = img ? (img.getAttribute('alt') || '').trim() : '';
    }
    return text || handle;
  }

  function isSaved(handle) {
    try {
      if (window.ROAffinity && typeof window.ROAffinity.isWishlisted === 'function') {
        return !!window.ROAffinity.isWishlisted(handle);
      }
      var raw = window.localStorage.getItem(AFFINITY_KEY);
      var data = raw ? JSON.parse(raw) : null;
      var list = data && Array.isArray(data.wishlist) ? data.wishlist : [];
      return list.some(function (entry) {
        return entry && entry.handle === handle;
      });
    } catch (e) {
      return false;
    }
  }

  /* One storage read for a whole repaint pass (isSaved() reads and parses
     storage per call). Same key and shape rockoff-affinity.js writes. Returns
     null when storage cannot be read; callers then fall back to isSaved(). */
  function savedSnapshot() {
    try {
      var raw = window.localStorage.getItem(AFFINITY_KEY);
      var data = raw ? JSON.parse(raw) : null;
      var list = data && Array.isArray(data.wishlist) ? data.wishlist : [];
      var set = Object.create(null);
      for (var i = 0; i < list.length; i++) {
        if (list[i] && list[i].handle) set[list[i].handle] = true;
      }
      return set;
    } catch (e) {
      return null;
    }
  }

  /* Repaint only the saved state of an existing heart (no measuring, no
     rebuilding). Writes only when something differs. Covers handles that left
     the wishlist without a click on their own heart (the 49th save evicts the
     oldest, saves from another tab). */
  function repaintSaved(heart, snapshot) {
    var handle = heart.getAttribute('data-ro-wishlist-handle') || '';
    if (!handle) return;
    var saved = snapshot ? !!snapshot[handle] : isSaved(handle);
    var l = labels();
    var label = heart.getAttribute('data-ro-wishlist-label') || handle;
    var pressed = saved ? 'true' : 'false';
    if (heart.getAttribute('aria-pressed') !== pressed) heart.setAttribute('aria-pressed', pressed);
    var aria = (saved ? l.remove : l.add) + ' ' + label;
    if (heart.getAttribute('aria-label') !== aria) heart.setAttribute('aria-label', aria);
    if (heart.classList.contains('is-wishlisted') !== saved) heart.classList.toggle('is-wishlisted', saved);
  }

  function svgIcon(modifier, filled) {
    var svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'ro-wishlist-btn__icon ro-wishlist-btn__icon--' + modifier);
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    if (filled) {
      svg.setAttribute('fill', 'currentColor');
    } else {
      svg.setAttribute('fill', 'none');
      svg.setAttribute('stroke', 'currentColor');
      svg.setAttribute('stroke-width', '2');
      svg.setAttribute('stroke-linecap', 'round');
      svg.setAttribute('stroke-linejoin', 'round');
    }
    var path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', HEART_PATH);
    svg.appendChild(path);
    return svg;
  }

  function buildHeart() {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ro-wishlist-btn ro-wishlist-btn--card ro-wishlist-btn--usf';
    btn.setAttribute('data-ro-wishlist-btn', '');
    btn.setAttribute(OWN_ATTR, '');
    btn.appendChild(svgIcon('empty', false));
    btn.appendChild(svgIcon('filled', true));
    /* Belt and braces with the delegated document handler in
       rockoff-affinity.js (which also preventDefaults): never let the click
       fall through to a card / image link. stopPropagation is deliberately
       NOT used, it would stop that delegated handler from ever running. */
    btn.addEventListener('click', function (evt) {
      evt.preventDefault();
    });
    return btn;
  }

  /* Point the heart at a product and paint its saved state. Only writes when
     something differs, so a repeat scan changes nothing. */
  function updateHeart(heart, handle, id, title) {
    var l = labels();
    var saved = isSaved(handle);
    var changed = false;

    function setAttr(name, value) {
      if (heart.getAttribute(name) !== value) {
        heart.setAttribute(name, value);
        changed = true;
      }
    }

    setAttr('data-ro-wishlist-handle', handle);
    setAttr('data-ro-wishlist-id', id);
    setAttr('data-ro-wishlist-label', title);
    setAttr('data-ro-wishlist-add-label', l.add);
    setAttr('data-ro-wishlist-remove-label', l.remove);
    setAttr('aria-pressed', saved ? 'true' : 'false');
    setAttr('aria-label', (saved ? l.remove : l.add) + ' ' + title);
    if (heart.classList.contains('is-wishlisted') !== saved) {
      heart.classList.toggle('is-wishlisted', saved);
    }
    return changed;
  }

  function imageBoxFor(card) {
    return (
      card.querySelector('.usf-sr-product__image-container') ||
      card.querySelector('.card__media') ||
      card.querySelector('.card__inner') ||
      card.querySelector('img:not(.ro-card-model-media)')
    );
  }

  function hostFor(card) {
    return card.querySelector('.card-wrapper') || imageBoxFor(card);
  }

  /* How far to pull the heart in so it stays top-right of the IMAGE. In the
     normal stacked card the image spans the wrapper (returns ''), so the theme
     CSS (top/right 0.8rem) applies. In a side-by-side (list view) card the
     image sits left of the text column, so pull the heart in to the image's
     right edge. READ ONLY (forces layout, so it is called in one batch for
     many cards, never interleaved with DOM writes). Returns null when the
     card cannot be measured yet (leave the heart as it is). */
  function rightFor(card, host) {
    var box = imageBoxFor(card);
    if (!box || box === host) return '';
    var hostRect = host.getBoundingClientRect();
    var boxRect = box.getBoundingClientRect();
    if (!hostRect.width || !boxRect.width) return null;
    var gap = hostRect.right - boxRect.right;
    return gap > 2 ? Math.round(gap + 8) + 'px' : '';
  }

  /* DOM writes only (no layout reads): make sure the card has exactly one
     heart, inside its host, pointed at the card's product. Returns the work
     item for the measuring passes, or null when there is nothing to do. */
  function prepareCard(card, handle) {
    /* A theme-rendered heart already lives here (theme cards reuse no
       .usf-sr-product class, but stay safe): leave the card alone. */
    if (card.querySelector('[data-ro-wishlist-btn]:not([' + OWN_ATTR + '])')) return null;

    var host = hostFor(card);
    var existing = card.querySelectorAll('[' + OWN_ATTR + ']');
    var i;
    if (!handle || !host) {
      /* Card not fully rendered yet (or a non-product tile): nothing to
         point a heart at, so drop any stale one from a reused node. */
      for (i = 0; i < existing.length; i++) existing[i].remove();
      card.removeAttribute(DONE_ATTR);
      card.__roHeart = null;
      return null;
    }

    /* Idempotent: exactly one heart per card. */
    for (i = 1; i < existing.length; i++) existing[i].remove();
    var heart = existing.length ? existing[0] : buildHeart();
    if (heart.parentNode !== host) host.appendChild(heart);
    updateHeart(heart, handle, idFor(card), titleFor(card, handle));
    card.__roHeart = heart;
    card.setAttribute(DONE_ATTR, handle);
    return { card: card, heart: heart, host: host };
  }

  /* A card is finished when it carries a heart for the product it shows now.
     Cheap attribute/property reads only. Vue reuses nodes for other products,
     which shows up as a different handle. */
  function isDone(card, handle) {
    var heart = card.__roHeart;
    return !!(
      handle &&
      heart &&
      heart.parentNode &&
      card.getAttribute(DONE_ATTR) === handle &&
      card.contains(heart)
    );
  }

  /* Incremental scan (1 Oct 2026 fix for Load more getting slower on every click:
     the old scan re-ran the whole per-card routine, including two layout
     reads interleaved with DOM writes, for EVERY card on EVERY re-render, so
     each click cost more than the last). Now only cards that are new, or that
     show a different product than before, are touched, in three batched
     passes (write, read, write) so the browser lays the page out at most
     twice per scan however many cards arrived. `force` (resize, pageshow,
     load) re-measures and repaints every card. */
  /* What decides heart placement besides the card itself: the grid/list view
     USF is showing. USF's view switch can change only classes and the
     stylesheet-driven image column, so a cached heart would keep its old
     offset; a changed signature forces the batched measuring pass. */
  var lastLayoutKey = null;
  function layoutKey(container) {
    var results = container.querySelector('.usf-results');
    var first = container.querySelector(CARD_SELECTOR);
    return [
      container.className,
      results ? results.className : '',
      first ? first.className : '',
      (window.location.search.match(/[?&]usf_view=([^&]*)/) || [])[1] || ''
    ].join('|');
  }

  /* Class-only view switches produce no childList mutation, so watch the
     class attribute of the container and its results list (two nodes, never
     the cards). Re-resolved on every scan because USF can replace them. */
  var viewObserver = null;
  var viewTargets = [];
  var onViewChange = function () {};
  function watchView(container) {
    if (typeof MutationObserver === 'undefined') return;
    if (!viewObserver) viewObserver = new MutationObserver(function () { onViewChange(); });
    var targets = [container, container.querySelector('.usf-results')];
    for (var t = 0; t < targets.length; t++) {
      if (targets[t] && viewTargets.indexOf(targets[t]) === -1) {
        viewTargets.push(targets[t]);
        viewObserver.observe(targets[t], { attributes: true, attributeFilter: ['class'] });
      }
    }
    if (viewTargets.length > 12) viewTargets = viewTargets.slice(-4);
  }

  function scan(force) {
    try {
      var container = getContainer();
      if (!container) return;
      watchView(container);
      var key = layoutKey(container);
      if (key !== lastLayoutKey) {
        lastLayoutKey = key;
        force = true;
      }
      var snapshot = savedSnapshot();
      var cards = container.querySelectorAll(CARD_SELECTOR);
      var work = [];
      var i;
      for (i = 0; i < cards.length; i++) {
        var card = cards[i];
        var handle = handleFor(card);
        if (!force && isDone(card, handle)) {
          /* Cached heart: skip building and measuring, but keep its saved
             state current (attribute reads, writes only on a difference). */
          repaintSaved(card.__roHeart, snapshot);
          continue;
        }
        var item = prepareCard(card, handle);
        if (item) work.push(item);
      }
      if (!work.length) return;

      /* Pass 2: reads only. */
      var needsPosition = [];
      var rights = [];
      for (i = 0; i < work.length; i++) {
        needsPosition[i] = window.getComputedStyle(work[i].host).position === 'static';
      }
      /* Pass 3: writes, then one batch of layout reads, then the writes. */
      for (i = 0; i < work.length; i++) {
        if (needsPosition[i]) work[i].host.style.position = 'relative';
      }
      for (i = 0; i < work.length; i++) {
        rights[i] = rightFor(work[i].card, work[i].host);
      }
      for (i = 0; i < work.length; i++) {
        var right = rights[i];
        if (right === null) continue;
        if (work[i].heart.style.right !== right) work[i].heart.style.right = right;
      }
    } catch (e) {
      /* never break the grid over a heart */
    }
  }

  function debounce(fn, delay) {
    var timer = null;
    return function () {
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        timer = null;
        fn();
      }, delay);
    };
  }

  /* True when a batch of mutation records could have changed the grid: the
     target is inside #usf_container, or contains it (USF can replace the
     container node). Records that only add our own hearts are ignored, so a
     scan never schedules the next scan, and page chrome (cart drawer, timers,
     sliders) no longer wakes the grid scan at all. */
  function relevant(records) {
    var container = getContainer();
    /* No container yet: nothing to scan. A late mount adds the container
       under some parent, and that record is relevant (target contains it). */
    if (!container) return false;
    for (var r = 0; r < records.length; r++) {
      var rec = records[r];
      var target = rec.target;
      if (!(container.contains(target) || (target.contains && target.contains(container)))) continue;
      var onlyOwn = rec.type === 'childList' && rec.addedNodes.length > 0 && rec.removedNodes.length === 0;
      if (onlyOwn) {
        for (var n = 0; n < rec.addedNodes.length; n++) {
          var node = rec.addedNodes[n];
          if (!(node.nodeType === 1 && node.hasAttribute(OWN_ATTR))) {
            onlyOwn = false;
            break;
          }
        }
      }
      if (!onlyOwn) return true;
    }
    return false;
  }

  function init() {
    /* Observers and listeners are installed even when #usf_container is not in
       the DOM yet (late mount); every scan looks the container up and returns
       at once when it is missing, and relevant() ignores page mutations until
       it exists, so non-USF pages pay nothing per mutation. */
    var forceNext = false;
    var run = debounce(function () {
      var force = forceNext;
      forceNext = false;
      scan(force);
    }, RESCAN_DELAY_MS);
    function check() {
      run();
    }
    function checkAll() {
      forceNext = true;
      run();
    }
    onViewChange = checkAll;
    var usfHooked = false;

    function hookUsfEvents() {
      if (usfHooked) return;
      try {
        if (window.usf && window.usf.event && typeof window.usf.event.add === 'function') {
          window.usf.event.add('sr_updated', check);
          usfHooked = true;
        }
      } catch (e) {
        /* the MutationObserver below still covers every re-render */
      }
    }

    if (typeof MutationObserver !== 'undefined' && document.body) {
      new MutationObserver(function (records) {
        if (relevant(records)) check();
      }).observe(document.body, { childList: true, subtree: true });
    }
    [400, 1500, 4000].forEach(function (ms) {
      window.setTimeout(function () {
        hookUsfEvents();
        check();
      }, ms);
    });
    window.addEventListener('load', function () {
      hookUsfEvents();
      checkAll();
    });
    window.addEventListener('resize', checkAll);
    /* Back/forward cache restore: repaint saved state from storage. */
    window.addEventListener('pageshow', checkAll);
    /* Saved state changes outside this grid: another tab (storage event), and
       a click on any wishlist heart (rockoff-affinity.js toggles that heart
       itself, but a 49th save evicts the oldest saved product, whose heart
       elsewhere in the grid must empty). Deferred one tick so the affinity
       click handler has written storage first. No measuring: scan() only
       repaints cached hearts. */
    window.addEventListener('storage', function (e) {
      if (!e.key || e.key === AFFINITY_KEY) check();
    });
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (t && t.closest && t.closest('[data-ro-wishlist-btn]')) window.setTimeout(check, 0);
    });
    hookUsfEvents();
    scan(true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
